/**
 * Add files: drop them on the zone, pick them, or paste screenshots anywhere on the page. On a
 * phone it also offers Take photo (the camera) and Photos (the photo library).
 * Each file uploads with a progress bar; a failed one says why and offers Retry. Finished
 * uploads go to onUploaded; the caller shows them (AttachmentList). Hub-agnostic: the caller
 * passes `upload`.
 */
import { AlertCircle, Camera, Images, Paperclip, RotateCcw, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { DragEvent, ReactNode } from 'react';
import { formatBytes } from '@orgus/protocol';
import { cn } from '../lib/cn.ts';
import { toUiError } from '../lib/errors.ts';
import { isCoarsePointer } from '../lib/platform.ts';
import type { UiError } from '../lib/errors.ts';
import { Button } from './Button.tsx';
import { IconButton } from './IconButton.tsx';

/** A stored attachment as the console shows it. */
export interface AttachmentView {
  id: string;
  name: string;
  size: number;
  mime: string;
  isImage: boolean;
  /** Shows the file (inline for images and text). */
  url: string;
  downloadUrl: string;
  thumbUrl?: string;
  width?: number;
  height?: number;
  by?: string;
  createdAt?: number;
}

export type UploadFn = (file: File, opts: { onProgress: (fraction: number) => void; signal: AbortSignal }) => Promise<AttachmentView>;

interface Pending {
  key: string;
  file: File;
  progress: number;
  error: UiError | null;
  ctrl: AbortController | null;
}

export interface AttachmentInputProps {
  upload: UploadFn;
  onUploaded: (a: AttachmentView) => void;
  /** 'document': screenshots pasted anywhere on the page are added (one input per view should use it). */
  paste?: 'document' | 'none';
  /** Files larger than this are refused before upload. */
  maxBytes?: number;
  /** Shown under the zone: limits, what happens to the files. */
  hint?: ReactNode;
  disabled?: boolean;
  /** A single-line zone for tight spaces (comment composers). */
  compact?: boolean;
  /**
   * 'button': no zone, only a paperclip (and the camera on a phone), for inside a Composer:
   * files dropped on the enclosing [data-drop-scope] attach, and uploads show as small chips.
   */
  variant?: 'zone' | 'button';
  /** Offer Take photo and Photos. Default: on touch-first devices. */
  camera?: boolean;
  className?: string;
}

let seq = 0;

/**
 * Files from a paste, a drop or a picker, with a name for pasted screenshots and camera photos
 * (they arrive as image.png or image.jpg).
 */
export function filesFrom(list: FileList | DataTransferItemList | null | undefined, now: Date = new Date(), prefix = 'screenshot'): File[] {
  if (!list) return [];
  const files: File[] = [];
  for (const x of Array.from(list as ArrayLike<File | DataTransferItem>)) {
    const f = x instanceof File ? x : (x as DataTransferItem).kind === 'file' ? (x as DataTransferItem).getAsFile() : null;
    if (f) files.push(f);
  }
  return files.map((f) => {
    if (f.name && !/^image\.(png|jpe?g)$/i.test(f.name)) return f;
    const stamp = now.toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '-');
    const ext = f.type === 'image/jpeg' ? 'jpg' : f.type.startsWith('image/') ? f.type.slice(6).replace(/[^a-z0-9]/g, '') || 'png' : 'bin';
    return new File([f], `${prefix}-${stamp}${files.length > 1 ? `-${++seq}` : ''}.${ext}`, { type: f.type });
  });
}

export function AttachmentInput({ upload, onUploaded, paste = 'none', maxBytes, hint, disabled, compact, variant = 'zone', camera, className }: AttachmentInputProps) {
  const [pending, setPending] = useState<Pending[]>([]);
  const [over, setOver] = useState(false);
  const [touch] = useState(isCoarsePointer);
  const photos = camera ?? touch;
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLSpanElement>(null);
  const id = useId();
  const live = useRef(true);
  // Set on every mount: React's development double mount runs the clean-up once in between,
  // which would otherwise leave every upload stuck at 0% and never handed to onUploaded.
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const patch = (key: string, p: Partial<Pending>) => live.current && setPending((list) => list.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const run = useCallback(
    async (key: string, file: File) => {
      const ctrl = new AbortController();
      patch(key, { ctrl, error: null, progress: 0 });
      try {
        const a = await upload(file, { onProgress: (f) => patch(key, { progress: Math.max(0, Math.min(1, f)) }), signal: ctrl.signal });
        if (!live.current) return;
        setPending((list) => list.filter((x) => x.key !== key));
        onUploaded(a);
      } catch (e) {
        if (ctrl.signal.aborted) return;
        patch(key, { error: toUiError(e, { what: `${file.name} did not upload.` }), ctrl: null });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [upload, onUploaded],
  );

  const add = useCallback(
    (files: File[]) => {
      if (disabled || !files.length) return;
      const next: Pending[] = files.map((file) => ({ key: `u${++seq}`, file, progress: 0, error: null, ctrl: null }));
      setPending((list) => [...list, ...next]);
      for (const p of next) {
        if (maxBytes && p.file.size > maxBytes) {
          patch(p.key, { error: { what: `${p.file.name} is too large.`, why: `It is ${formatBytes(p.file.size)}; the limit is ${formatBytes(maxBytes)}.`, next: 'Crop or compress it, or zip it, then add it again.' } });
          continue;
        }
        if (!p.file.size) {
          patch(p.key, { error: { what: `${p.file.name} is empty.`, why: 'It has no bytes to upload.', next: 'Choose another file.' } });
          continue;
        }
        void run(p.key, p.file);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, maxBytes, run],
  );

  useEffect(() => {
    if (paste !== 'document' || disabled) return;
    const onPaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented) return;
      // A paste inside another dialog (a form opened over this view) belongs to that dialog.
      const mine = (zoneRef.current ?? buttonRef.current)?.closest('[role="dialog"]') ?? null;
      const target = e.target instanceof Element ? e.target.closest('[role="dialog"]') : null;
      if (target && target !== mine) return;
      const files = filesFrom(e.clipboardData?.items);
      if (!files.length) return;
      e.preventDefault();
      add(files);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [paste, disabled, add]);

  // A button inside a composer: the composer is the drop target, lit while files are over it.
  useEffect(() => {
    if (variant !== 'button' || disabled) return;
    const scope = buttonRef.current?.closest<HTMLElement>('[data-drop-scope]');
    if (!scope) return;
    const hasFiles = (e: globalThis.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const over = (e: globalThis.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      scope.setAttribute('data-drop-over', '');
    };
    const leave = (e: globalThis.DragEvent) => {
      if (e.relatedTarget instanceof Node && scope.contains(e.relatedTarget)) return;
      scope.removeAttribute('data-drop-over');
    };
    const drop = (e: globalThis.DragEvent) => {
      scope.removeAttribute('data-drop-over');
      if (!hasFiles(e)) return;
      e.preventDefault();
      add(filesFrom(e.dataTransfer?.files));
    };
    scope.addEventListener('dragover', over);
    scope.addEventListener('dragleave', leave);
    scope.addEventListener('drop', drop);
    return () => {
      scope.removeEventListener('dragover', over);
      scope.removeEventListener('dragleave', leave);
      scope.removeEventListener('drop', drop);
      scope.removeAttribute('data-drop-over');
    };
  }, [variant, disabled, add]);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    add(filesFrom(e.dataTransfer?.files));
  };

  const cancel = (p: Pending) => {
    p.ctrl?.abort();
    setPending((list) => list.filter((x) => x.key !== p.key));
  };

  const pickers = (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        aria-label="Choose files to attach"
        onChange={(e) => {
          add(filesFrom(e.target.files));
          e.target.value = '';
        }}
      />
      {photos && (
        <>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            aria-label="Take a photo to attach"
            onChange={(e) => {
              add(filesFrom(e.target.files, new Date(), 'photo'));
              e.target.value = '';
            }}
          />
          <input
            ref={libraryRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            aria-label="Choose photos to attach"
            onChange={(e) => {
              add(filesFrom(e.target.files));
              e.target.value = '';
            }}
          />
        </>
      )}
    </>
  );

  if (variant === 'button') {
    const failed = pending.filter((p) => p.error);
    return (
      <>
        <span ref={buttonRef} className={cn('flex items-center', className)}>
          <IconButton label={hint ? `Attach files. ${String(hint)}` : 'Attach files'} icon={<Paperclip />} size="sm" disabled={disabled} onClick={() => inputRef.current?.click()} />
          {photos && (
            <>
              <IconButton label="Take photo" icon={<Camera />} size="sm" disabled={disabled} onClick={() => cameraRef.current?.click()} />
              <IconButton label="Photos" icon={<Images />} size="sm" disabled={disabled} onClick={() => libraryRef.current?.click()} />
            </>
          )}
          {pickers}
        </span>
        {pending.map((p) => (
          <span
            key={p.key}
            className={cn('inline-flex h-6 max-w-48 items-center gap-1 rounded-pill border pl-2 pr-0.5 text-caption', p.error ? 'border-status-danger text-status-danger' : 'border-border-subtle text-fg-muted')}
            title={p.error ? `${p.error.what} ${p.error.why} ${p.error.next}` : undefined}
          >
            {p.error ? <AlertCircle className="size-3 shrink-0" aria-hidden /> : <Upload className="size-3 shrink-0 text-fg-subtle" aria-hidden />}
            <span className="min-w-0 truncate">{p.file.name}</span>
            <span className="shrink-0 text-fg-subtle num">{p.error ? 'Failed' : `${Math.round(p.progress * 100)}%`}</span>
            {p.error && !(maxBytes && p.file.size > maxBytes) && p.file.size > 0 && <IconButton label={`Retry ${p.file.name}`} icon={<RotateCcw />} size="xs" onClick={() => void run(p.key, p.file)} />}
            <IconButton label={p.error ? `Remove ${p.file.name}` : `Cancel ${p.file.name}`} icon={<X />} size="xs" onClick={() => cancel(p)} />
          </span>
        ))}
        {failed.length > 0 && (
          <span role="alert" className="w-full text-caption text-status-danger">
            {failed[0]!.error!.what} {failed[0]!.error!.next}
          </span>
        )}
      </>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        ref={zoneRef}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cn(
          'flex items-center gap-3 rounded-md border border-dashed text-sm transition-colors duration-150',
          compact ? 'px-2 py-1.5' : 'px-3 py-3',
          over ? 'border-accent bg-accent-soft text-fg' : 'border-border text-fg-subtle',
          disabled && 'opacity-50',
        )}
        data-dropzone=""
      >
        <span className="flex shrink-0 text-fg-subtle [&_svg]:size-4" aria-hidden>
          {over ? <Upload /> : <Paperclip />}
        </span>
        <span className={cn('min-w-0 flex-1', photos && 'sr-only')}>
          {over ? 'Drop to attach' : paste === 'document' ? 'Drop files here, paste a screenshot, or' : 'Drop files here, or'}
        </span>
        {photos && (
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" icon={<Camera />} onClick={() => cameraRef.current?.click()} disabled={disabled}>
              Take photo
            </Button>
            <Button size="sm" variant="outline" icon={<Images />} onClick={() => libraryRef.current?.click()} disabled={disabled}>
              Photos
            </Button>
          </span>
        )}
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={disabled} aria-describedby={hint ? `${id}-hint` : undefined}>
          {photos ? 'Files' : 'Choose files'}
        </Button>
        {pickers}
      </div>
      {hint && (
        <p id={`${id}-hint`} className="text-sm text-fg-subtle">
          {hint}
        </p>
      )}
      {pending.length > 0 && (
        <ul className="flex flex-col gap-1.5" aria-live="polite">
          {pending.map((p) => (
            <li key={p.key} className="flex flex-col gap-1 rounded-md border border-border-subtle bg-surface px-2.5 py-1.5">
              <div className="flex items-center gap-2 text-sm">
                {p.error ? <AlertCircle className="size-4 shrink-0 text-failed" aria-hidden /> : <Upload className="size-4 shrink-0 text-fg-subtle" aria-hidden />}
                <span className="min-w-0 flex-1 truncate text-fg">{p.file.name}</span>
                <span className="shrink-0 text-fg-subtle num">{p.error ? 'Failed' : `${Math.round(p.progress * 100)}% of ${formatBytes(p.file.size)}`}</span>
                {p.error && !(maxBytes && p.file.size > maxBytes) && p.file.size > 0 && (
                  <IconButton label={`Retry ${p.file.name}`} icon={<RotateCcw />} size="xs" onClick={() => void run(p.key, p.file)} />
                )}
                <IconButton label={p.error ? `Remove ${p.file.name}` : `Cancel ${p.file.name}`} icon={<X />} size="xs" onClick={() => cancel(p)} />
              </div>
              {p.error ? (
                <p className="text-sm text-failed">
                  {p.error.what} {p.error.why} {p.error.next}
                </p>
              ) : (
                <div className="h-1 overflow-hidden rounded-full bg-hover" role="progressbar" aria-label={`Uploading ${p.file.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(p.progress * 100)}>
                  <div className="h-full rounded-full bg-accent transition-[width] duration-150" style={{ width: `${Math.max(2, p.progress * 100)}%` }} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
